import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CalendlyService } from 'src/app/services/calendly.service';
import { IconifyService } from 'src/app/services/iconify.service';
import { environment } from 'src/environments/environment';

interface CapabilityService {
  id: string;
  name: string;
  desc: string;
  icon: string;
}

@Component({
  selector: 'app-capabilities',
  imports: [RouterModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './capabilities.component.html',
  styleUrls: ['./capabilities.component.css']
})
export class CapabilitiesComponent implements OnInit {

  public calendlyUrl = environment.CALENDLY_URL;
  services: CapabilityService[] = [
    {
      id: 'enterprise-cloud-migration',
      name: 'Enterprise Cloud Migration',
      desc: 'Accelerate digital transformation with predictable, automated, zero-downtime enterprise migrations.',
      icon: 'tdesign:cloud'
    },
    {
      id: 'automated-finops',
      name: 'Automated FinOps',
      desc: 'Unify multi-cloud governance and eliminate infrastructure waste with data-driven FinOps intelligence.',
      icon: 'solar:dollar-linear'
    },
    {
      id: 'cloud-native-sase',
      name: 'Cloud-Native SASE',
      desc: 'Consolidate disconnected perimeter security into a single, cloud-agnostic, single-vendor network fabric.',
      icon: 'heroicons-outline:shield-check'
    },
    {
      id: 'platform-engineering',
      name: 'Platform Engineering',
      desc: 'Transition from unmanaged virtual machines to resilient, fully-managed cloud platform services.',
      icon: 'tdesign:code'
    },
    {
      id: 'document-intelligence',
      name: 'Document Intelligence',
      desc: 'Process unstructured documents instantly using Spatial Computer Vision and NLP. Automate massive data-entry pipelines with near-perfect accuracy.',
      icon: 'heroicons-outline:document-text'
    },
    {
      id: 'internal-knowledge-bots',
      name: 'Internal Knowledge Bots',
      desc: 'A unified conversational Enterprise Brain. Synthesize answers directly from your scattered internal data silos with click-through citations.',
      icon: 'tabler:bulb'
    },
    {
      id: 'agent-assist-copilot',
      name: 'Agent Assist Copilot',
      desc: 'Real-time AI copilot processing live call audio and chat in sub-second intervals to instantly surface answers, resolve disputes, and reduce handle times.',
      icon: 'lucide:video'
    },
    {
      id: 'agentic-automation',
      name: 'Agentic Automation',
      desc: 'Deploy autonomous L0 agents that interface directly with enterprise APIs to intercept, reason, and resolve complex backend workflows without human intervention.',
      icon: 'ion:flash-outline'
    },
    {
      id: 'llm-orchestration',
      name: 'LLM Orchestration',
      desc: 'Securely fine-tune foundational models using strict Retrieval-Augmented Generation (RAG) pipelines, allowing your AI to speak your corporate language accurately.',
      icon: 'tdesign:cloud'
    },
    {
      id: 'cognitive-infrastructure',
      name: 'Cognitive Infrastructure',
      desc: 'The 5% foundational layer. We engineer high-availability data lakes and GPU-optimized architectures required to train, deploy, and scale heavy machine learning models.',
      icon: 'mage:dashboard'
    },
    {
      id: 'onboarding-orchestration',
      name: 'Onboarding Orchestration',
      desc: 'Transform fragmented employee onboarding into a zero-touch, AI-orchestrated workflow connecting HR, ITSM, and endpoint management systems seamlessly.',
      icon: 'grommet-icons:group'
    },
    {
      id: 'managed-services',
      name: 'AI-Led Managed Services',
      desc: 'Replace reactive, labor-intensive IT support with a bespoke, AI-autonomous framework ensuring your operations run intelligently 24/7/365.',
      icon: 'uil:setting'
    },
    {
      id: 'zero-trust-ai-security',
      name: 'Zero-Trust AI Security',
      desc: 'Prevent proprietary data bleed. We enforce strict cryptographic guardrails and compliance monitoring to safely deploy models inside your tenant.',
      icon: 'codicon:lock'
    },
    {
      id: 'applied-ai-strategy',
      name: 'Applied AI Strategy',
      desc: 'Navigate the complexities of enterprise AI adoption with executive-level strategic consulting, feasibility audits, and intelligent workflow mapping.',
      icon: 'bx:message'
    },
    {
      id: 'dynamics-365-copilot',
      name: 'Dynamics 365 Copilot',
      desc: 'Overcome operational drag by integrating L0 autonomous agents and L1 augmented Copilot systems directly into your Microsoft Dynamics 365 ecosystem.',
      icon: 'ep:files'
    }
  ];

  contactForm = new FormGroup({
    name: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    company: new FormControl(''),
    message: new FormControl('', Validators.required)
  });

  isSubmitting = false;
  submitted = false;

  constructor(
    private router: Router,
    private calendly: CalendlyService,
    private iconify: IconifyService
  ) { }

  ngOnInit(): void {
    this.iconify.ensureLoaded().catch(() => undefined);
  }

  navigateToService(serviceId: string): void {
    this.router.navigate(['/services', serviceId]);
    this.scrollToTop();
  }

  openCalendlyPopup() {
    return this.calendly.openPopup(this.calendlyUrl);
  }

  onSubmit(): void {
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    setTimeout(() => {
      this.isSubmitting = false;
      this.submitted = true;
      this.contactForm.reset();
      setTimeout(() => {
        this.submitted = false;
      }, 5000);
    }, 1500);
  }

  scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }

}
